import { ChangeEvent, KeyboardEvent } from 'react';
import * as Yup from 'yup';
import TextField from '@mui/material/TextField';
import Switch from 'src/components/Switch';
import CheckboxGroup from 'src/components/CheckboxGroup';
import AutoCompleteWithFreeText from 'src/components/AutoComplete/AutoCompleteWithFreeText';
import { nanToUndefined } from 'src/utils';
import {
  SolutionAdxTablesField,
  SolutionItemField,
  SolutionMenderSettingsFields,
  SolutionStorageAccountSettingsFields,
} from '../types';

// Helper function to prevent invalid characters in number fields
const preventInvalidNumberChars = (e: KeyboardEvent<HTMLInputElement>) => {
  const { key } = e;
  const { value, selectionStart } = e.currentTarget;

  // Block 'e', 'E', '+', '-', '.' characters explicitly
  if (['e', 'E', '+', '-', '.'].includes(key)) {
    e.preventDefault();
    return;
  }

  // Allow navigation keys
  const allowedKeys = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'Tab',
    'Enter',
    'Home',
    'End',
  ];
  if (allowedKeys.includes(key)) {
    return;
  }

  // Prevent leading zeros when the field is empty
  if (key === '0' && !value && selectionStart === 0) {
    e.preventDefault();
    return;
  }

  // Only allow digits
  if (!/^\d$/.test(key)) {
    e.preventDefault();
  }
};

// Helper function to handle number input changes
const handleNumberInput = (
  e: ChangeEvent<HTMLInputElement>,
  handler?: (event: ChangeEvent<HTMLInputElement>) => void
) => {
  const { target } = e;
  const { value } = target;

  // Handle empty input
  if (!value) {
    if (handler) handler(e);
    return;
  }

  const numValue = parseInt(value, 10);

  if (numValue > 35600) {
    target.value = '35600';
  } else if (numValue <= 0) {
    target.value = '';
  } else {
    target.value = numValue.toString(); // This removes any leading zeros
  }

  if (handler) handler(e);
};

export const getSolutionSettingsMenderFormFields = (
  menderEnabled?: boolean,
  isPlatformReader?: boolean,
  isValidSolutionUser?: boolean,
  isConsumerSolution?: boolean,
  onMenderEnabledChange?: (event: ChangeEvent<HTMLInputElement>) => void
) => [
  {
    name: SolutionMenderSettingsFields.Enabled,
    label: 'Enable Mender',
    Component: Switch,
    disabled:
      isPlatformReader ||
      isValidSolutionUser ||
      (!menderEnabled && isConsumerSolution),
    type: 'checkbox',
    onChange: onMenderEnabledChange,
    labelStyle: { color: '#7B7B7B', opacity: '80%' },
  },
  {
    name: SolutionMenderSettingsFields.ReleasePrefix,
    label: 'Release prefix',
    Component: TextField,
    placeholder: 'Enter release prefix',
    disabled: !menderEnabled || isPlatformReader || isValidSolutionUser,
    required: menderEnabled,
    validation: menderEnabled
      ? Yup.string().required('Release prefix name is required')
      : undefined,
  },
];

export const getSolutionSettingsStorageAccountFormFields = (
  onSourceStorageEnabledChange?: (event: ChangeEvent<HTMLInputElement>) => void
) => [
  {
    name: SolutionStorageAccountSettingsFields.Enabled,
    label: 'Enable Source Storage',
    Component: Switch,
    type: 'checkbox',
    onChange: onSourceStorageEnabledChange,
  },
];

export const getSolutionSettingsCacheRetentionFormFields = (
  cachePeriod?: number,
  retentionPeriod?: number,
  disableCacheLimit?: boolean,
  disableRetentionLimit?: boolean,
  handleCacheLimit?: (event: ChangeEvent<HTMLInputElement>) => void,
  handleRetentionLimit?: (event: ChangeEvent<HTMLInputElement>) => void,
  handleUnlimitedCache?: (event: ChangeEvent<HTMLInputElement>) => void,
  handleUnlimitedRetention?: (event: ChangeEvent<HTMLInputElement>) => void,
  disableCacheRetention?: boolean,
  isPlatformReader?: boolean,
  isDataIngested?: boolean,
  isConsumerSolution?: boolean
) => [
  {
    name: SolutionItemField.CachePeriod,
    label: 'Cache Period',
    placeholder: 'Enter the values in days',
    value: disableCacheLimit ? '' : cachePeriod || '',
    initialValue: '',
    onChange: (e) => handleNumberInput(e, handleCacheLimit),
    onKeyDown: preventInvalidNumberChars,
    InputProps: {
      inputProps: {
        min: 1,
        max: 35600,
        pattern: '[0-9]*',
        inputMode: 'numeric',
      },
    },
    Component: TextField,
    validation: disableCacheLimit
      ? Yup.string().notRequired()
      : Yup.number()
          .transform(nanToUndefined)
          .integer('Only whole numbers are allowed')
          .min(1, 'Cache period must be greater than 0 and smaller than 35600')
          .max(
            35600,
            'Cache period must be greater than 0 and smaller than 35600'
          )
          .required('Cache Period is required')
          .typeError('Only digits are allowed')
          .test(
            'no-leading-zero',
            'Leading zeros are not allowed',
            (value) => value === undefined || !/^0+/.test(String(value))
          ),
    type: 'number',
    required: !disableCacheLimit,
    disabled: disableCacheLimit || isPlatformReader || isDataIngested,
  },
  {
    name: SolutionItemField.UnlimitedCache,
    Component: CheckboxGroup,
    type: 'checkbox-group',
    required: false,
    labelStyle: { mr: 3 },
    onChange: handleUnlimitedCache,
    disabled: disableCacheRetention || isPlatformReader || isDataIngested,
    options: [
      { label: 'Unlimited days for Cache period', value: 'UnlimitedCache' },
    ],
  },
  {
    name: SolutionItemField.RetentionPeriod,
    label: 'Retention Period',
    placeholder: 'Enter the values in days',
    value: disableRetentionLimit ? '' : retentionPeriod || '',
    initialValue: '',
    onChange: (e) => handleNumberInput(e, handleRetentionLimit),
    onKeyDown: preventInvalidNumberChars,
    InputProps: {
      inputProps: {
        min: 1,
        max: 35600,
        pattern: '[0-9]*',
        inputMode: 'numeric',
      },
    },
    Component: TextField,
    validation: disableRetentionLimit
      ? Yup.string().notRequired()
      : Yup.number()
          .transform(nanToUndefined)
          .integer('Only whole numbers are allowed')
          .min(
            1,
            'Retention Period must be greater than 0 and smaller than 35600'
          )
          .max(
            35600,
            'Retention Period must be greater than 0 and smaller than 35600'
          )
          .required('Retention Period is required')
          .typeError('Only digits are allowed')
          .test(
            'no-leading-zero',
            'Leading zeros are not allowed',
            (value) => value === undefined || !/^0+/.test(String(value))
          ),
    type: 'number',
    required: !disableRetentionLimit,
    disabled: disableRetentionLimit || isPlatformReader || isDataIngested,
  },
  {
    name: SolutionItemField.UnlimitedRetention,
    Component: CheckboxGroup,
    type: 'checkbox-group',
    required: false,
    onChange: handleUnlimitedRetention,
    labelStyle: { mr: 3 },
    disabled: disableCacheRetention || isPlatformReader || isDataIngested,
    options: [
      {
        label: 'Unlimited days for Retention period',
        value: 'UnlimitedRetention',
      },
    ],
  },
];

export const getSolutionSettingsADXTableFormFields = (
  isEditMode: boolean,
  adxTables: string,
  cachePeriod?: number,
  retentionPeriod?: number,
  handleCacheLimit?: (event: ChangeEvent<HTMLInputElement>) => void,
  handleRetentionLimit?: (event: ChangeEvent<HTMLInputElement>) => void,
  isPlatformReader?: boolean,
  isDataIngested?: boolean
) => [
  {
    name: SolutionAdxTablesField.TableName,
    label: 'Name',
    Component: TextField,
    required: true,
    disabled: isEditMode || isPlatformReader,
    validation: Yup.string()
      .required('Name is required')
      .matches(
        /^[A-Za-z][A-Za-z0-9_-]*$/,
        'Name must start with a letter and contain only alphanumeric, hyphen, and underscore characters'
      ),
    sx: { width: ({ spacing }) => spacing(56) },
  },
  {
    name: SolutionAdxTablesField.CachePeriod,
    label: 'Cache Period',
    placeholder: 'Enter the values in days',
    value: cachePeriod || '',
    initialValue: '',
    onChange: (e) => handleNumberInput(e, handleCacheLimit),
    onKeyDown: preventInvalidNumberChars,
    InputProps: {
      inputProps: {
        min: 1,
        max: 35600,
        pattern: '[0-9]*',
        inputMode: 'numeric',
      },
    },
    sx: { width: ({ spacing }) => spacing(56) },
    Component: TextField,
    type: 'number',
    validation: Yup.number()
      .transform(nanToUndefined)
      .integer('Only whole numbers are allowed')
      .min(1, 'Cache period must be greater than 0 and smaller than 35600')
      .max(35600, 'Cache period must be greater than 0 and smaller than 35600')
      .typeError('Only digits are allowed')
      .test(
        'no-leading-zero',
        'Leading zeros are not allowed',
        (value) => value === undefined || !/^0+/.test(String(value))
      ),
    disabled: isPlatformReader || isDataIngested,
  },
  {
    name: SolutionAdxTablesField.RetentionPeriod,
    label: 'Retention Period',
    placeholder: 'Enter the values in days',
    value: retentionPeriod || '',
    initialValue: '',
    onChange: (e) => handleNumberInput(e, handleRetentionLimit),
    onKeyDown: preventInvalidNumberChars,
    InputProps: {
      inputProps: {
        min: 1,
        max: 35600,
        pattern: '[0-9]*',
        inputMode: 'numeric',
      },
    },
    sx: { width: ({ spacing }) => spacing(56) },
    Component: TextField,
    type: 'number',
    validation: Yup.number()
      .transform(nanToUndefined)
      .integer('Only whole numbers are allowed')
      .min(1, 'Retention Period must be greater than 0 and smaller than 35600')
      .max(
        35600,
        'Retention Period must be greater than 0 and smaller than 35600'
      )
      .typeError('Only digits are allowed')
      .test(
        'no-leading-zero',
        'Leading zeros are not allowed',
        (value) => value === undefined || !/^0+/.test(String(value))
      ),
    disabled: isPlatformReader || isDataIngested,
  },
];
